// /src/pages/private/members/personalhub/dashboard/cards/GameloopTagsCard.jsx
// input-security: verified — fields: tagIds chip selection (integer IDs only, no free-text)

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSecurity } from '../../../../../../SecurityContext';
import {
  fetchDashboardGameloopTags,
  saveDashboardGameloopTags,
} from "../dashboardGameloopTagsApi";
import { sanitizeTagIds } from "../../../../../../utils/sanitizeUserTextInput";
import DashboardStructureStrip from "./DashboardStructureStrip";
import { IconPen, IconCheck } from "../dashboardGameloopIcons";

/**
 * Gameloop interest tags sourced from backend `UserGameloopPreference` (+ `MasterTag`).
 */
export default function GameloopTagsCard({
  handleId,
  player,
  loading: profileLoading,
  onLayoutEditChange,
}) {
  const { subjectHasPermission, realUserHasPermission, isViewAsActive } =
    useSecurity();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [editing, setEditing] = useState(false);

  const [masterTags, setMasterTags] = useState([]);
  const [limits, setLimits] = useState({
    min: 2,
    max: 5,
    poolLargeEnoughForQuota: false,
  });
  const [selectedTags, setSelectedTags] = useState([]);
  const [draftIds, setDraftIds] = useState(() => new Set());
  const [pendingChangeRequest, setPendingChangeRequest] = useState(null);
  const [lastResolvedChangeRequest, setLastResolvedChangeRequest] = useState(null);
  const [activeAssignment, setActiveAssignment] = useState(null);
  const [membershipAssignmentId, setMembershipAssignmentId] = useState(null);
  const [membershipAssignmentName, setMembershipAssignmentName] = useState(null);
  const [saveInfo, setSaveInfo] = useState("");
  const [structureEditing, setStructureEditing] = useState(false);

  const canEditUi = (() => {
    const hasWrite = (check) =>
      check("private.navbar.personalhub.gameloopTags", "write") ||
      check("private.navbar.personalhub", "write");
    return isViewAsActive
      ? hasWrite(realUserHasPermission)
      : hasWrite(subjectHasPermission);
  })();

  const hid = Number(handleId);
  const hidOk = Number.isInteger(hid) && hid >= 1;

  const reload = useCallback(async () => {
    if (!hidOk) {
      setMasterTags([]);
      setSelectedTags([]);
      setLimits((p) => ({ ...p, poolLargeEnoughForQuota: false }));
      return;
    }

    const hasCachedTags = masterTags.length > 0 || selectedTags.length > 0;
    if (!hasCachedTags) {
      setLoading(true);
    }
    setFetchError("");
    try {
      const data = await fetchDashboardGameloopTags(hid);
      if (!data?.ok) {
        setFetchError(data?.error || "Could not load tags.");
        setMasterTags([]);
        setSelectedTags([]);
        return;
      }
      setMasterTags(Array.isArray(data.masterTags) ? data.masterTags : []);
      setSelectedTags(Array.isArray(data.selectedTags) ? data.selectedTags : []);
      setPendingChangeRequest(
        data.pendingChangeRequest && typeof data.pendingChangeRequest === "object"
          ? data.pendingChangeRequest
          : null
      );
      setLastResolvedChangeRequest(
        data.lastResolvedChangeRequest &&
          typeof data.lastResolvedChangeRequest === "object"
          ? data.lastResolvedChangeRequest
          : null
      );
      setActiveAssignment(
        data.activeAssignment && typeof data.activeAssignment === "object"
          ? data.activeAssignment
          : null
      );
      const mid =
        data.membershipAssignmentId != null ? Number(data.membershipAssignmentId) : null;
      setMembershipAssignmentId(Number.isInteger(mid) && mid > 0 ? mid : null);
      setMembershipAssignmentName(
        data.membershipAssignmentName != null
          ? String(data.membershipAssignmentName).trim() || null
          : null
      );
      if (data.limits && typeof data.limits === "object") {
        setLimits((prev) => ({
          min:
            typeof data.limits.min === "number" ? data.limits.min : prev.min,
          max:
            typeof data.limits.max === "number" ? data.limits.max : prev.max,
          poolLargeEnoughForQuota:
            typeof data.limits.poolLargeEnoughForQuota === "boolean"
              ? data.limits.poolLargeEnoughForQuota
              : prev.poolLargeEnoughForQuota,
        }));
      }
    } catch (e) {
      setFetchError(e?.message || "Could not load tags.");
      setMasterTags([]);
      setSelectedTags([]);
    } finally {
      setLoading(false);
    }
  }, [hid, hidOk]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    if (typeof onLayoutEditChange !== "function") return;
    onLayoutEditChange({
      structureEditing,
      tagsEditing: editing,
    });
  }, [structureEditing, editing, onLayoutEditChange]);

  const poolOk = limits.poolLargeEnoughForQuota;
  const minSel = limits.min;
  const maxSel = limits.max;
  const selectedCount = draftIds.size;
  const selectionValid =
    !poolOk || (selectedCount >= minSel && selectedCount <= maxSel);

  const hasPending = Boolean(pendingChangeRequest?.id);

  const openEdit = useCallback(() => {
    setSaveError("");
    setSaveInfo("");
    const next = new Set(
      selectedTags.map((t) => t.id).filter((id) => Number.isInteger(id) && id > 0)
    );
    setDraftIds(next);
    setEditing(true);
  }, [selectedTags]);

  const toggleDraftId = useCallback(
    (tagId) => {
      setDraftIds((prev) => {
        const next = new Set(prev);
        if (next.has(tagId)) {
          next.delete(tagId);
        } else if (next.size < maxSel) {
          next.add(tagId);
        }
        return next;
      });
    },
    [maxSel]
  );

  const handleSave = useCallback(async () => {
    if (!hidOk || !selectionValid || saving) return;
    setSaving(true);
    setSaveError("");
    setSaveInfo("");
    try {
      const tagIds = sanitizeTagIds([...draftIds]);
      const data = await saveDashboardGameloopTags(hid, tagIds);
      if (!data?.ok) {
        setSaveError(data?.error || "Could not save tags.");
        return;
      }
      setSelectedTags(
        Array.isArray(data.selectedTags) ? data.selectedTags : []
      );
      setSaveInfo("");
      setEditing(false);
      setDraftIds(new Set());
    } catch (e) {
      setSaveError(e?.message || "Could not save tags.");
    } finally {
      setSaving(false);
    }
  }, [hid, hidOk, draftIds, selectionValid, saving]);

  const selectedIdSet = useMemo(() => {
    const s = new Set();
    for (const t of selectedTags) {
      const id = Number(t?.id);
      if (Number.isInteger(id) && id > 0) {
        s.add(id);
      }
    }
    return s;
  }, [selectedTags]);

  if (!hidOk) {
    return null;
  }

  const showPen = !editing && !loading && canEditUi && poolOk;

  const readonlyTagList =
    masterTags.length > 0 ? masterTags : selectedTags;

  const showReadonlyAllTags = !editing && readonlyTagList.length > 0;

  const showEditToggleList =
    editing && Array.isArray(masterTags) && masterTags.length > 0;

  return (
    <section
      className={
        "member-dashboard__gameloop-card" +
        (structureEditing || editing ? " member-dashboard__gameloop-card--expanded" : "")
      }
      aria-label="Gameplay interest tags"
      data-resource="private.navbar.personalhub.gameloopTags"
      data-action="read"
      data-description="Personal hub → Gameloop tags"
    >
      <DashboardStructureStrip
        handleId={handleId}
        player={player}
        loading={profileLoading}
        canEdit={canEditUi}
        onEditingChange={setStructureEditing}
        pendingChangeRequest={pendingChangeRequest}
        lastResolvedChangeRequest={lastResolvedChangeRequest}
        activeAssignment={
          pendingChangeRequest?.activeAssignment || activeAssignment
        }
        membershipAssignmentId={membershipAssignmentId}
        membershipAssignmentName={membershipAssignmentName}
        onPendingChangeRequest={setPendingChangeRequest}
        onStructureSaved={(data) => {
          const savedId =
            data?.membershipAssignmentId != null
              ? Number(data.membershipAssignmentId)
              : null;
          if (Number.isInteger(savedId) && savedId > 0) {
            setMembershipAssignmentId(savedId);
          }
          if (data?.membershipAssignmentName) {
            setMembershipAssignmentName(String(data.membershipAssignmentName));
          }
          reload();
        }}
        onResolvedMessageAck={() => {
          setLastResolvedChangeRequest(null);
        }}
      />

      <div className="member-dashboard__gameloop-tags-section">
        <div className="member-dashboard__gameloop-card-head">
        <div
          className={
            "member-dashboard__gameloop-heading" +
            (editing && poolOk
              ? " member-dashboard__gameloop-heading--editing"
              : "")
          }
        >
          <h2 className="member-dashboard__gameloop-title">Interest tags</h2>
          {editing && poolOk ? (
            <p
              className="member-dashboard__gameloop-count member-dashboard__gameloop-count--head"
              aria-live="polite"
            >
              {selectedCount} / {maxSel} selected
              {selectedCount < minSel
                ? ` — pick at least ${minSel - selectedCount} more.`
                : ""}
            </p>
          ) : null}
        </div>

        <div className="member-dashboard__gameloop-actions">
          {showPen ? (
            <button
              type="button"
              className="member-dashboard__gameloop-icon-btn"
              onClick={openEdit}
              aria-label="Edit interest tags"
              data-resource="private.navbar.personalhub.gameloopTags"
              data-action="execute"
              data-route="/api/user/private-dashboard/gameloop-tags-save"
              data-description="Open tag editor"
            >
              <IconPen className="member-dashboard__gameloop-icon" />
            </button>
          ) : null}

          {editing ? (
            <button
              type="button"
              className={
                "member-dashboard__gameloop-validate" +
                (selectionValid && !saving
                  ? ""
                  : " member-dashboard__gameloop-validate--disabled")
              }
              disabled={saving || !selectionValid}
              onClick={handleSave}
              aria-label={
                saving ? "Saving tags" : "Validate tag selection"
              }
              data-resource="private.navbar.personalhub.gameloopTags"
              data-action="write"
              data-route="/api/user/private-dashboard/gameloop-tags-save"
              data-description="Save gameloop tags"
            >
              {saving ? (
                <span className="member-dashboard__gameloop-validate-dots">
                  …
                </span>
              ) : (
                <IconCheck className="member-dashboard__gameloop-icon member-dashboard__gameloop-icon--check" />
              )}
            </button>
          ) : null}
        </div>
      </div>

      <div className="member-dashboard__gameloop-tags-body">
      {fetchError ? (
        <p
          className="member-dashboard__gameloop-msg member-dashboard__gameloop-msg--error"
          role="alert"
        >
          {fetchError}
        </p>
      ) : null}
      {saveError ? (
        <p
          className="member-dashboard__gameloop-msg member-dashboard__gameloop-msg--error"
          role="alert"
        >
          {saveError}
        </p>
      ) : null}

      {saveInfo ? (
        <p
          className="member-dashboard__gameloop-msg member-dashboard__gameloop-msg--info"
          role="status"
        >
          {saveInfo}
        </p>
      ) : null}

      {loading && !editing && masterTags.length === 0 ? (
        <p className="member-dashboard__gameloop-loading">Loading tags…</p>
      ) : null}

      {showReadonlyAllTags ? (
        <ul className="member-dashboard__gameloop-chips" role="list">
          {readonlyTagList.map((t) => {
            const id = Number(t.id);
            if (!Number.isInteger(id) || id < 1) return null;
            const label = String(t.label || "").trim() || `(#${id})`;
            const isPicked = selectedIdSet.has(id);
            return (
              <li key={id} className="member-dashboard__gameloop-chip-li">
                <span
                  className={
                    "member-dashboard__gameloop-chip" +
                    (isPicked
                      ? " member-dashboard__gameloop-chip--picked-readonly"
                      : "")
                  }
                >
                  {label}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}

      {showEditToggleList ? (
        <>
          <ul
            className="member-dashboard__gameloop-chips"
            role="listbox"
            aria-multiselectable="true"
          >
            {masterTags.map((t) => {
              const id = Number(t.id);
              if (!Number.isInteger(id) || id < 1) return null;
              const label = String(t.label || "").trim() || `(#${id})`;
              const isSelected = draftIds.has(id);
              const atMax = draftIds.size >= maxSel;
              const locked = !isSelected && atMax;
              return (
                <li key={id} className="member-dashboard__gameloop-chip-li">
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    disabled={locked}
                    className={
                      "member-dashboard__gameloop-chip member-dashboard__gameloop-chip--toggle" +
                      (isSelected ? " member-dashboard__gameloop-chip--selected" : "") +
                      (locked ? " member-dashboard__gameloop-chip--locked" : "")
                    }
                    onClick={() => toggleDraftId(id)}
                    data-resource="private.navbar.personalhub.gameloopTags"
                    data-action="write"
                    data-route="/api/user/private-dashboard/gameloop-tags-save"
                    data-description="Toggle draft gameloop tag"
                  >
                    {label}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      ) : null}

      {!loading && editing && masterTags.length === 0 ? (
        <p className="member-dashboard__gameloop-msg">
          No tags are available right now.
        </p>
      ) : null}

      {!loading && !editing && masterTags.length === 0 && !fetchError ? (
        <p className="member-dashboard__gameloop-empty">
          No tags are configured for this dashboard yet.
        </p>
      ) : null}
      </div>
      </div>
    </section>
  );
}
